const projects = [
  {
    name: "XP-Agents",
    category: "Agent Framework",
    status: "3,000+ commits",
    repo: "https://github.com/paulingalls/xp-agents",
    description:
      "“Extreme Programming in a box” — a Claude Code plugin that makes AI coding agents, solo or in parallel teams, follow disciplined engineering practices like TDD, code review, and planning automatically, keeping multiple agents in sync through a shared event log.",
    tags: ["Claude Code", "Multi-agent", "Python", "Plugin"],
  },
  {
    name: "SealTeam",
    category: "AI Agents",
    repo: "https://github.com/paulingalls/sealteam",
    description:
      "A command-line tool that turns a plain-English goal into a self-organizing team of up to a dozen Claude agents that plan, execute, and critique their own work — coordinating over shared queues and a git workspace, and even authoring new tools for themselves at runtime.",
    tags: ["Claude API", "Multi-agent", "Bun", "TypeScript", "Redis"],
  },
  {
    name: "react-native-realtime-audio",
    category: "Voice AI",
    status: "Published to npm",
    repo: "https://github.com/paulingalls/react-native-realtime-audio",
    description:
      "An Expo / React Native module for streaming, recording, and visualizing audio in real time — the microphone front-end for voice-AI apps. Ships on-device voice-activity detection (Silero VAD via ONNX) so an agent knows when a user is actually speaking.",
    tags: ["React Native", "Swift", "Kotlin", "ONNX", "Silero VAD"],
  },
  {
    name: "Land2Port",
    category: "Computer Vision",
    repo: "https://github.com/paulingalls/land2port",
    description:
      "A Rust command-line tool that reframes landscape video into vertical 9:16 by detecting and tracking the people in each frame with YOLO object-detection models, with hardware-accelerated inference across CoreML, CUDA, and TensorRT. It powers VodShorter's reframing.",
    tags: ["Rust", "YOLO", "ONNX Runtime", "CoreML", "FFmpeg"],
  },
  {
    name: "TS-Flow",
    category: "AI Workflow Engine",
    status: "Open source",
    repo: "https://github.com/paulingalls/ts-flow",
    description:
      "A JSON-defined, event-driven workflow engine where AI is a first-class building block: chat, image, transcription, and embedding nodes compose alongside scraping and posting steps to power automated content pipelines that run several real apps.",
    tags: ["TypeScript", "OpenAI", "Turborepo", "Event-driven"],
  },
  {
    name: "Legacy",
    category: "AI Product · Voice",
    status: "In development",
    description:
      "An AI biographer that interviews people in natural voice conversations and preserves their life stories — so descendants can listen, and even talk with them, decades later. Built on a real-time speech-to-LLM-to-speech pipeline and a multi-pass narrative-analysis engine grounded in research psychology.",
    tags: ["Claude", "LiveKit", "Voice AI", "pgvector", "TypeScript", "Python"],
  },
  {
    name: "Divine Ruin",
    category: "AI Product · Games",
    status: "Active build",
    repo: "https://github.com/paulingalls/divineruin",
    description:
      "A voice-first MMORPG where thousands of players share one persistent world, each guided by a personal AI Dungeon Master. The DM pairs a real-time voice agent with a deterministic rules engine — the model decides when to narrate, the code decides what actually happens.",
    tags: ["Claude", "LiveKit", "Deepgram", "Inworld", "Python", "PostgreSQL"],
  },
  {
    name: "VodShorter",
    category: "AI Product · Video",
    status: "Production",
    description:
      "Upload a full podcast episode and get back short, vertical, social-ready clips. Claude selects the best moments from a word-level transcript via structured tool calls, then hands off to a computer-vision engine for smart cropping and auto-captions.",
    tags: ["Claude", "Whisper", "FFmpeg", "Bun", "PostgreSQL"],
  },
  {
    name: "ContactForge",
    category: "AI Product · macOS",
    description:
      "A native Mac app that untangles duplicate contacts across your accounts. Deterministic matching handles the easy cases on-device; Claude resolves the genuinely ambiguous “is this the same person?” calls, returning validated golden-record decisions with confidence scores.",
    tags: ["Swift", "SwiftUI", "Claude", "Zod", "Bun"],
  },
  {
    name: "Clipcast",
    category: "Agent-Native API",
    repo: "https://github.com/paulingalls/clipcast",
    description:
      "A pay-per-request API that turns structured input into polished animated social videos — built for autonomous AI agents to discover and pay for directly via the x402 protocol, with a planned MCP server to expose it as a native agent tool.",
    tags: ["Bun", "Hono", "x402", "MCP", "Playwright"],
  },
  {
    name: "Beacon",
    category: "Agent-Native Infrastructure",
    repo: "https://github.com/paulingalls/beacon",
    description:
      "A self-hosted, cookie-free analytics platform designed to be queried by AI instead of dashboards. A schema-introspection API and MCP server let Claude answer ad-hoc analytics questions in natural language across a portfolio of products.",
    tags: ["Bun", "Hono", "PostgreSQL", "MCP"],
  },
  {
    name: "J Walk",
    category: "Built with AI",
    status: "App Store ready",
    description:
      "A polished iOS and Android coaching app for the “Japanese walking” interval routine, with voice cues that fire even when the phone is locked or silent — designed and built end-to-end with AI coding tools.",
    tags: ["Expo", "React Native", "TypeScript", "StoreKit"],
  },
  {
    name: "SimplyHuman",
    category: "Human in the Age of AI",
    description:
      "A verified-human social network — a deliberate counterpoint to autonomous agents and synthetic content. Every post carries a cryptographic guarantee that a specific real person stands behind it, using proof-of-personhood and hardware-backed device signing.",
    tags: ["Bun", "World ID", "Secure Enclave", "Drizzle"],
  },
];

export function Projects() {
  return (
    <section id="projects" className="section section-alt">
      <div className="section-inner">
        <h2 className="section-title">Building with AI</h2>
        <p className="projects-intro">
          Over the past few years I've been building intensively with AI —
          shipping products powered by large language models, voice agents, and
          computer vision; creating infrastructure for the emerging world of
          autonomous agents; and using AI coding tools to build faster than
          ever. A selection of recent work:
        </p>
        <div className="project-grid">
          {projects.map((project) => (
            <div className="project-card" key={project.name}>
              <div className="project-card-head">
                <span className="project-category">{project.category}</span>
                {project.status && (
                  <span className="project-status">{project.status}</span>
                )}
              </div>
              <h3 className="project-name">
                {"repo" in project && project.repo ? (
                  <a
                    href={project.repo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-name-link"
                  >
                    {project.name}
                    <svg
                      className="project-repo-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 .5C5.37.5 0 5.87 0 12.5c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58v-2.03c-3.34.73-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5.99.11-.78.42-1.3.76-1.6-2.67-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.3-.54-1.52.11-3.18 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.65 1.66.24 2.88.12 3.18.77.84 1.23 1.91 1.23 3.22 0 4.61-2.8 5.63-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12.01 12.01 0 0 0 24 12.5C24 5.87 18.63.5 12 .5z" />
                    </svg>
                  </a>
                ) : (
                  project.name
                )}
              </h3>
              <p className="project-description">{project.description}</p>
              <div className="project-tags">
                {project.tags.map((tag) => (
                  <span className="project-tag" key={tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
